// apps/client/src/pages/app/components/ActivityEditor.tsx
//
// Schema-driven activity editor. Structural fields (name, type, budget) stay
// as dedicated MUI inputs; all narrative answers are owned by RDTIFormRenderer
// rendering the FieldDef[] for `coreActivity` or `supportingActivity` from
// the project's RDTI schema version.

import {
  Project,
  RDTIResponses,
  ResearchActivity,
  ResearchActivityType,
  UserRole,
} from '@avanzu/types';
import { Delete as DeleteIcon, Save as SaveIcon, ScienceOutlined } from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { resolveSchemaVersionForTaxYear } from '@avanzu/rdti-schema';
import { EmptyStateContent } from '../../../components/EmptyStateContent';
import { RDTIFormRenderer } from '../../../components/rdti/RDTIFormRenderer';
import { useAuth } from '../../../contexts/AuthContext';
import { useProject } from '../../../contexts/ProjectContext';
import { apiClient } from '../../../services';
import AiFieldEnhancer from './AiFieldEnhancer';

interface ActivityEditorProps {
  activity: ResearchActivity | null;
  project: Project | null;
  onActivityUpdate: (activity: ResearchActivity) => void;
  onActivityDelete: (activity: ResearchActivity) => void;
}

const ActivityEditor: React.FC<ActivityEditorProps> = ({
  activity,
  project,
  onActivityUpdate,
  onActivityDelete,
}) => {
  const [formData, setFormData] = useState<Partial<ResearchActivity>>({});
  const [responses, setResponses] = useState<RDTIResponses>({});
  const [transactionCount, setTransactionCount] = useState<number>(0);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
  const { currentOrganisation } = useAuth();
  const { currentProjectId } = useProject();
  const isViewer = currentOrganisation?.role === UserRole.VIEWER;

  // Activity inherits the project's schemaVersion. Fall back to the taxYear
  // mapping if the activity row doesn't carry one yet (legacy data).
  const schemaVersion =
    activity?.schemaVersion ??
    project?.schemaVersion ??
    (project ? resolveSchemaVersionForTaxYear(project.taxYear) : 'FY25');

  const section: 'coreActivity' | 'supportingActivity' =
    formData.type === ResearchActivityType.SUPPORTING ? 'supportingActivity' : 'coreActivity';

  useEffect(() => {
    if (activity) {
      setFormData(activity);
      setResponses(activity.responses ?? {});
      fetchTransactionCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity]);

  const fetchTransactionCount = async () => {
    if (!activity || !currentOrganisation?.id || !currentProjectId) {
      setTransactionCount(0);
      return;
    }

    try {
      setLoadingTransactions(true);
      const response = await apiClient.get(
        `/organisations/${currentOrganisation.id}/projects/${currentProjectId}/activities/${activity.id}/transactions`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }

      const data = await response.json();
      setTransactionCount(data.transactions?.length || 0);
    } catch (error) {
      console.error('Error fetching transaction count:', error);
      setTransactionCount(0);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Build the dynamic option list for support.relatedCoreActivityIds /
  // support.howSupportsCore: sibling core activities in the same project.
  const dynamicOptions = useMemo(() => {
    // Sibling lookup is fetched at the page level — leave empty for now.
    // Wired up by the parent Activities page in a follow-up.
    return undefined as Record<string, { value: string; label: string }[]> | undefined;
  }, []);

  if (!activity) {
    return (
      <EmptyStateContent
        icon={<ScienceOutlined />}
        title="Select an activity to edit"
        message=" Choose an activity from the list to view and edit its details"
      />
    );
  }

  const handleInputChange = <K extends keyof ResearchActivity>(field: K, value: ResearchActivity[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (formData && activity) {
      onActivityUpdate({
        ...activity,
        ...formData,
        responses,
        updatedAt: new Date(),
      } as ResearchActivity);
    }
  };

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Typography variant="h6">Activity Editor</Typography>
        <Chip size="small" label={schemaVersion} variant="outlined" />
        {activity.aiGenerated && (
          <Chip
            icon={<AutoFixHighIcon />}
            label="AI Generated"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          name="activityName"
          id="activityName"
          label="Activity Name"
          fullWidth
          value={formData.name || ''}
          onChange={(e) => handleInputChange('name', e.target.value)}
          disabled={isViewer}
        />
        <FormControl sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Activity Type
          </Typography>
          <RadioGroup
            value={formData.type || ResearchActivityType.CORE}
            onChange={(e) => handleInputChange('type', e.target.value as ResearchActivityType)}
            row
          >
            <FormControlLabel
              value={ResearchActivityType.CORE}
              control={<Radio />}
              label="Core Activity"
              disabled={isViewer}
            />
            <FormControlLabel
              value={ResearchActivityType.SUPPORTING}
              control={<Radio />}
              label="Supporting Activity"
              disabled={isViewer}
            />
          </RadioGroup>
        </FormControl>
        <TextField
          name="budgetAmount"
          id="budgetAmount"
          sx={{ maxWidth: '50%' }}
          label="Budget Amount"
          type="text"
          value={formData.budgetAmount || 0}
          onChange={(e) => handleInputChange('budgetAmount', parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          disabled={isViewer}
        />
        <Divider />

        <Typography variant="body2" color="text.secondary">
          AusIndustry {schemaVersion} {section === 'coreActivity' ? 'Core' : 'Supporting'} activity
          questions
        </Typography>

        <RDTIFormRenderer
          schemaVersion={schemaVersion}
          section={section}
          responses={responses}
          onChange={setResponses}
          dynamicOptions={dynamicOptions}
          disabled={isViewer}
          renderFieldAccessory={(field) =>
            currentOrganisation?.id && currentProjectId && project ? (
              <AiFieldEnhancer
                organisationId={currentOrganisation.id}
                projectId={currentProjectId}
                fieldName={field.key}
                currentValue={(responses[field.key] as string) || ''}
                projectContext={{
                  name: project.name,
                  industrySector: project.industrySector || undefined,
                  activityName: formData.name || activity?.name,
                  activityType: formData.type || activity?.type,
                }}
                onApply={(enhanced) =>
                  setResponses((prev) => ({ ...prev, [field.key]: enhanced }))
                }
                disabled={isViewer}
                quotaAiEnabled={currentOrganisation?.quotaAiEnabled}
              />
            ) : null
          }
        />

        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2" fontWeight={600} color="text.primary" gutterBottom>
                Allocated Transactions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {loadingTransactions
                  ? 'Loading transaction count...'
                  : `${transactionCount} ${transactionCount === 1 ? 'transaction' : 'transactions'} allocated to this activity`}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: transactionCount > 0 ? 'primary.main' : 'grey.300',
                color: 'white',
              }}
            >
              {loadingTransactions ? (
                <CircularProgress size={28} sx={{ color: 'white' }} />
              ) : (
                <Typography variant="h5" fontWeight={600}>
                  {transactionCount}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
        <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isViewer}
          >
            Save Activity
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onActivityDelete(activity)}
            disabled={transactionCount > 0 || isViewer}
            title={
              isViewer
                ? 'Viewers cannot delete activities'
                : transactionCount > 0
                  ? `Cannot delete activity with ${transactionCount} allocated ${transactionCount === 1 ? 'transaction' : 'transactions'}`
                  : 'Delete activity'
            }
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ActivityEditor;
